import { RtkEmptySplitApi } from "../../types";
import { ChatMessageModel } from "./models/ChatMessage";
import { ChatMessagesApiFetchParams } from "./payloads";
import { ChatsApiTags } from "./tags";

export const addChatsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [ChatsApiTags.getMessages, ChatsApiTags.pin],
  });

  const chatsApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      messages: builder.query<
        { data: ChatMessageModel[]; remaining_item: boolean },
        {
          params: ChatMessagesApiFetchParams;
        }
      >({
        query: ({ params }) => ({
          url: `/api/auth/messages`,
          params: {
            ...params,
            time: params.time ? params.time.replace("T", " ") : undefined,
          },
          transformResponse: (response: string) => {
            const parsedResponse: {
              data: ChatMessageModel[];
              remaining_item: boolean;
            } = JSON.parse(response);
            return {
              ...parsedResponse,
              data: parsedResponse.data,
            };
          },
        }),
        providesTags: [ChatsApiTags.getMessages],
      }),
      getPinnedMessages: builder.query<
        { data: ChatMessageModel[] },
        {
          entityId: string;
          chatId: string;
        }
      >({
        query: ({ entityId, chatId }) => ({
          url: `/api/auth/messages/${entityId}/pin`,
          params: {
            chat_id: chatId,
          },
        }),
        providesTags: (_result, _error, { chatId }) => [
          { type: ChatsApiTags.pin, id: chatId },
        ],
      }),
      pinMessages: builder.mutation<
        void,
        {
          entityId: string;
          chatId: string;
          msgReceiptIds: string[];
          pin: boolean;
        }
      >({
        query: ({ entityId, chatId, msgReceiptIds, pin }) => ({
          url: `/api/auth/messages/${entityId}/pin`,
          method: "PUT",
          data: {
            pin,
            chat_id: chatId,
            msg_receipt_ids: msgReceiptIds,
          },
          silentError: true,
        }),
        invalidatesTags: (_result, _error, { chatId, pin }) =>
          pin ? [{ type: ChatsApiTags.pin, id: chatId }] : [],
        onQueryStarted: async (
          { pin, chatId, entityId, msgReceiptIds },
          { dispatch, queryFulfilled }
        ) => {
          if (pin) return;

          try {
            await queryFulfilled;

            dispatch(
              chatsApi.util.updateQueryData(
                "getPinnedMessages",
                { entityId, chatId },
                (pinnedMessagesDraft) => {
                  pinnedMessagesDraft.data = pinnedMessagesDraft.data.filter(
                    (pinnedMessage) =>
                      !msgReceiptIds.includes(pinnedMessage.msg_receipt_id)
                  );
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),
      message: builder.query<
        { data: ChatMessageModel[]; remaining_item: boolean },
        {
          msg_id: string;
        }
      >({
        query: ({ msg_id }) => ({
          url: `/api/auth/messages/msg_id/${msg_id}`,
          silentError: true,
          transformResponse: (response: string) => {
            const parsedResponse: {
              data: ChatMessageModel[];
              remaining_item: boolean;
            } = JSON.parse(response);
            return {
              ...parsedResponse,
              data: parsedResponse.data,
            };
          },
        }),
      }),
      deleteConversation: builder.mutation<
        void,
        { entityId: string; chatId: string }
      >({
        query: ({ chatId, entityId }) => ({
          url: "/api/auth/messages/delete_conversation",
          method: "DELETE",
          data: { entity_id: entityId, workspace_id: chatId },
        }),
      }),
    }),
  });

  return chatsApi;
};

export type ChatsApi = ReturnType<typeof addChatsApi>;
